import React, { useState, useMemo } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, IconButton, Box, Chip, Tabs, Tab, Stack, Divider,
    MenuItem, TextField, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BugReportIcon from '@mui/icons-material/BugReport';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import InfoIcon from '@mui/icons-material/Info';
import SortIcon from '@mui/icons-material/Sort';

import { formatMoney, formatDate } from '../ProducerDashboardHelper';
import { OneCommTotalPendingTransactions } from '../../interfaces/ProducerDashboard';
import TransferDocumentation from './TransferDocumentation';
import { getCfg } from './TransactionConfig';

interface Props {
    data: OneCommTotalPendingTransactions;
    currency: string;
}

export default function OneCommTransactionsContent({ data, currency }: Props) {
    const [tabIndex, setTabIndex] = useState(0);
    const [operatorFilter, setOperatorFilter] = useState<string>('ALL');
    const [sortAsc, setSortAsc] = useState(false);
    const [expanded, setExpanded] = useState<boolean>(false);

    const availableTypes = useMemo(() => data?.pending_transactions ? Object.keys(data.pending_transactions) : [], [data]);
    const currentType = availableTypes[tabIndex];
    const currentStats = data?.pending_transactions ? data.pending_transactions[currentType] : null;

    const displayComms = useMemo(() => {
        if (!currentStats) return [];
        let list = [...currentStats.pending_transactions];
        if (operatorFilter !== 'ALL') list = list.filter(c => c.operator_name === operatorFilter);
        return list.sort((a, b) => {
            const dateA = new Date(a.operator_event_date).getTime();
            const dateB = new Date(b.operator_event_date).getTime();
            return sortAsc ? dateA - dateB : dateB - dateA;
        });
    }, [currentStats, operatorFilter, sortAsc]);

    const availableOperators = useMemo(() => {
        if (!currentStats) return [];
        return Array.from(new Set(currentStats.pending_transactions.map(c => String(c.operator_name)))).sort();
    }, [currentStats]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', bgcolor: '#fff' }}>
            {/* DOCUMENTATION SECTION */}
            <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} sx={{ boxShadow: 'none', borderBottom: '1px solid #eee', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}>
                    <Typography variant="caption" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                        <InfoIcon fontSize="small" color="primary" sx={{ fontSize: 16 }} /> TRANSFER FLOW DOCUMENTATION
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: '#fafafa', p: 2 }}>
                    <TransferDocumentation />
                </AccordionDetails>
            </Accordion>

            {/* TABS & FILTERS BAR */}
            <Box sx={{ borderBottom: '1px solid #eee', px: 2, bgcolor: '#fff' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Tabs
                        value={tabIndex}
                        onChange={(_, v) => setTabIndex(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            minHeight: 44,
                            '& .MuiTab-root': { fontSize: '0.65rem', fontWeight: 800, minHeight: 44, textTransform: 'uppercase' }
                        }}
                    >
                        {availableTypes.map((type) => (
                            <Tab
                                key={type}
                                label={`${type.replace(/_/g, ' ')} (${data.pending_transactions[type].count_by_type})`}
                                icon={type.includes('PAYMENT') ? <PaymentsIcon sx={{ fontSize: 14 }} /> : type.includes('REFUND') ? <AssignmentReturnIcon sx={{ fontSize: 14 }} /> : type.includes('FEE') ? <BugReportIcon sx={{ fontSize: 14 }} /> : <ReceiptLongIcon sx={{ fontSize: 14 }} />}
                                iconPosition="start"
                            />
                        ))}
                    </Tabs>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            select
                            size="small"
                            variant="standard"
                            value={operatorFilter}
                            onChange={(e) => setOperatorFilter(e.target.value)}
                            sx={{
                                width: 100,
                                '& .MuiSelect-select': {
                                    fontSize: '0.65rem',
                                    fontWeight: 500,
                                    color: 'text.secondary',
                                    py: 0.5
                                }
                            }}
                            InputProps={{ disableUnderline: true }}
                        >
                            <MenuItem value="ALL" sx={{ fontSize: '0.65rem' }}>All Operators</MenuItem>
                            {availableOperators.map(op => <MenuItem key={op} value={op} sx={{ fontSize: '0.65rem' }}>{op}</MenuItem>)}
                        </TextField>
                        <Divider orientation="vertical" flexItem sx={{ height: 16, my: 'auto', mx: 0.5 }} />
                        <IconButton size="small" onClick={() => setSortAsc(!sortAsc)}>
                            <SortIcon sx={{ fontSize: 14, color: 'text.secondary', transform: sortAsc ? 'none' : 'rotate(180deg)' }} />
                        </IconButton>
                    </Stack>
                </Stack>
            </Box>

            {/* SUBTOTALS BAR */}
            {currentStats && (
                <Box sx={{
                    px: 3, py: 0.8,
                    bgcolor: '#f8f9fa',
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: getCfg(currentType).color }} />
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.6rem', textTransform: 'uppercase' }}>
                                {currentType?.replace(/_/g, ' ')} SUMMARY
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={3} divider={<Divider orientation="vertical" flexItem sx={{ height: 12, my: 'auto' }} />}>
                        <Stack direction="row" spacing={1} alignItems="baseline">
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.disabled', fontSize: '0.55rem' }}>FOR PRODUCER:</Typography>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#2e7d32', fontVariantNumeric: 'tabular-nums' }}>
                                {formatMoney(currentStats.pending_from_escrow_for_producer, currency)}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="baseline">
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.disabled', fontSize: '0.55rem' }}>FOR OPERATOR:</Typography>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#1976d2', fontVariantNumeric: 'tabular-nums' }}>
                                {formatMoney(currentStats.pending_from_escrow_for_operator + currentStats.pending_from_producer_for_operator, currency)}
                            </Typography>
                        </Stack>
                    </Stack>
                </Box>
            )}

            {/* TABLE SECTION */}
            <TableContainer sx={{ flexGrow: 1, overflowY: 'auto' }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow sx={{ "& th": { bgcolor: '#f8f9fa', fontWeight: 800, fontSize: '0.6rem', color: 'text.secondary', borderBottom: '1px solid #eee' } }}>
                            <TableCell sx={{ pl: 3 }}>ID / ITEM</TableCell>
                            <TableCell>TYPE</TableCell>
                            <TableCell>OPERATOR</TableCell>
                            <TableCell>DATES</TableCell>
                            <TableCell>STATUS</TableCell>
                            <TableCell align="right">AMOUNT</TableCell>
                            <TableCell align="right">PROD. PART</TableCell>
                            <TableCell align="right">OPER. PART</TableCell>
                            <TableCell align="right" sx={{ pr: 3 }}>VAT</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayComms.map((com) => {
                            const cfg = getCfg(com.type);
                            return (
                                <TableRow key={com.id} hover sx={{ '& td': { py: 0.8, borderBottom: '1px solid #f5f5f5' } }}>
                                    <TableCell sx={{ borderLeft: `3px solid ${cfg.color}`, pl: 3 }}>
                                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, fontFamily: 'monospace' }}>{com.id}</Typography>
                                        <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled' }}>{com.item_id}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={cfg.label}
                                            size="small"
                                            variant="outlined"
                                            sx={{ height: 18, fontSize: '0.5rem', color: cfg.color, borderColor: cfg.color, fontWeight: 700, borderRadius: '4px' }}
                                        />
                                    </TableCell>
                                    <TableCell><Typography sx={{ fontSize: '0.6rem', fontWeight: 600 }}>{com.operator_name}</Typography></TableCell>
                                    <TableCell>
                                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, display: 'block' }}>{formatDate(com.operator_event_date)}</Typography>
                                        <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled' }}>Cr: {formatDate(com.created_date)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{ fontSize: '0.55rem', textTransform: 'uppercase', fontWeight: 700, color: 'text.secondary' }}>
                                            {com.transfer_status.toLowerCase().replace(/_/g, ' ')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.65rem', fontWeight: 500, color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
                                        {formatMoney(com.amount, currency)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>{formatMoney(com.producer_part, currency)}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>{formatMoney(com.operator_part, currency)}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.65rem', fontWeight: 600, pr: 3 }}>{formatMoney(com.operator_vat_amount, currency)}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}